import React, { lazy, Suspense, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';

import { LoadingState } from '@components';
import { useSingleColumn, useNavigation, AppPaths } from '@hooks';
import { ProtectedRoute } from '@components/AccessControl';
import { PROTECT_PAGES_BY_ROLES, TeamRole } from '@configs/constants';

// Lazy load form components
const EditDemographics = lazy(() => import('./EditDemographics').then((module) => ({ default: module.EditDemographics })));
const ProfileCard = lazy(() => import('./ProfileCard').then((module) => ({ default: module.ProfileCard })));
const AdvisorEditContactDetails = lazy(() =>
  import('./EditContactDetails').then((module) => ({ default: module.AdvisorEditContactDetails })),
);
const AdvisorAboutForm = lazy(() => import('./AboutForm').then((module) => ({ default: module.AdvisorAboutForm })));
const TeamAboutForm = lazy(() => import('./AboutForm').then((module) => ({ default: module.TeamAboutForm })));
const AdvisorEditTestimonial = lazy(() => import('./Testimonials').then((module) => ({ default: module.AdvisorEditTestimonial })));
const TeamEditTestimonials = lazy(() => import('./Testimonials').then((module) => ({ default: module.TeamEditTestimonials })));
const AdvisorReorderTestimonials = lazy(() =>
  import('./Testimonials/ReorderTestimonial/AdvisorReorderTestimonials').then((module) => ({
    default: module.AdvisorReorderTestimonials,
  })),
);
const TeamReorderTestimonials = lazy(() =>
  import('./Testimonials/ReorderTestimonial/TeamReorderTestimonials').then((module) => ({
    default: module.TeamReorderTestimonials,
  })),
);
const AddEditRoles = lazy(() => import('./Roles/AddEditRoles').then((module) => ({ default: module.AddEditRoles })));
const EditMeetTheTeam = lazy(() => import('./EditMeetTheTeam').then((module) => ({ default: module.EditMeetTheTeam })));
const TeamEditContactDetails = lazy(() => import('./EditContactDetails').then((module) => ({ default: module.TeamEditContactDetails })));

export const FormRouter = ({ isTeamView }: { isTeamView?: boolean }) => {
  // Check if user is on mobile
  const { isSingleColumn, isLoading } = useSingleColumn();
  const { navigateToPath } = useNavigation();
  const location = useLocation();

  // Redirect mobile users away from testimonial pages
  useEffect(() => {
    if (isSingleColumn) {
      const isTestimonialsPage = location.pathname.includes('/testimonials');
      if (isTestimonialsPage) {
        navigateToPath(AppPaths.PROFILE_VIEW, { relative: false });
      }
    }
  }, [isSingleColumn, location.pathname]);

  return (
    <Suspense fallback={<LoadingState />}>
      <Routes>
        <Route
          path={`demographics`}
          element={
            <ProtectedRoute userRequiredRoles={[]} otherUserRequiredRoles={null} useDefaultRoles={false}>
              <EditDemographics />
            </ProtectedRoute>
          }
        />
        <Route path={`profile-card`} element={<ProfileCard />} />
        <Route
          path={'contact-details'}
          element={
            isTeamView ? (
              <ProtectedRoute
                userRequiredRoles={[TeamRole.TEAM_ADMIN, ...PROTECT_PAGES_BY_ROLES.OtherUserProfilePage]}
                otherUserRequiredRoles={PROTECT_PAGES_BY_ROLES.OtherUserProfilePage}
                context="team"
                useDefaultRoles={false}
              >
                <TeamEditContactDetails />
              </ProtectedRoute>
            ) : (
              <AdvisorEditContactDetails />
            )
          }
        />
        <Route
          path={'about'}
          element={
            isTeamView ? (
              <ProtectedRoute
                userRequiredRoles={[TeamRole.TEAM_ADMIN, ...PROTECT_PAGES_BY_ROLES.OtherUserProfilePage]}
                otherUserRequiredRoles={PROTECT_PAGES_BY_ROLES.OtherUserProfilePage}
                context="team"
                useDefaultRoles={false}
              >
                <TeamAboutForm />
              </ProtectedRoute>
            ) : (
              <AdvisorAboutForm />
            )
          }
        />
        <Route
          path={`testimonials`}
          element={
            isLoading ? null : (
              <ProtectedRoute
                userRequiredRoles={isTeamView ? [TeamRole.TEAM_ADMIN, ...PROTECT_PAGES_BY_ROLES.OtherUserProfilePage] : undefined}
                otherUserRequiredRoles={isTeamView ? PROTECT_PAGES_BY_ROLES.OtherUserProfilePage : undefined}
                context={isTeamView ? 'team' : 'user'}
                useDefaultRoles={!isTeamView}
              >
                {isTeamView ? <TeamEditTestimonials /> : <AdvisorEditTestimonial />}
              </ProtectedRoute>
            )
          }
        />
        <Route
          path={`testimonials/reorder`}
          element={
            isLoading ? null : (
              <ProtectedRoute
                userRequiredRoles={isTeamView ? [TeamRole.TEAM_ADMIN, ...PROTECT_PAGES_BY_ROLES.OtherUserProfilePage] : undefined}
                otherUserRequiredRoles={isTeamView ? PROTECT_PAGES_BY_ROLES.OtherUserProfilePage : undefined}
                context={isTeamView ? 'team' : 'user'}
                useDefaultRoles={!isTeamView}
              >
                {isTeamView ? <TeamReorderTestimonials /> : <AdvisorReorderTestimonials />}
              </ProtectedRoute>
            )
          }
        />
        <Route
          path={`roles`}
          element={
            <ProtectedRoute
              userRequiredRoles={[]}
              otherUserRequiredRoles={PROTECT_PAGES_BY_ROLES.OtherUserProfilePage}
              useDefaultRoles={false}
            >
              <AddEditRoles />
            </ProtectedRoute>
          }
        />
        <Route
          path={`meet-the-team`}
          element={
            isLoading ? null : (
              <ProtectedRoute
                userRequiredRoles={[TeamRole.TEAM_MEMBER, ...PROTECT_PAGES_BY_ROLES.OtherUserProfilePage]}
                otherUserRequiredRoles={PROTECT_PAGES_BY_ROLES.OtherUserProfilePage}
                context="team"
                useDefaultRoles={false}
              >
                <EditMeetTheTeam />
              </ProtectedRoute>
            )
          }
        />
      </Routes>
    </Suspense>
  );
};
